// =============================================================================
// Slideshow Express — per-style text-overlay settings (shared client/server)
// -----------------------------------------------------------------------------
// The server-side compositor (server/services/slideshow-text-overlay.ts) burns
// each slide's copy onto the clean KIE image using a hardcoded per-style look.
// Masters can now override that look per style from Manage Styles; the choices
// are stored as a JSON blob on slideshow_express_styles.text_settings and
// validated with the schema below on BOTH ends.
//
// EVERY field is optional. An unset field falls back to the style's existing
// hardcoded default, so a style with no saved settings (or a partially filled
// one) renders byte-identical to today for the untouched knobs.
// =============================================================================

import { z } from "zod";

export const SLIDESHOW_TEXT_ALIGNMENTS = ["left", "center", "right"] as const;
export type SlideshowTextAlignment = (typeof SLIDESHOW_TEXT_ALIGNMENTS)[number];

export const SLIDESHOW_TEXT_PLACEMENTS = [
  "upper",
  "upperThird",
  "center",
  "belowCenter",
] as const;
export type SlideshowTextPlacement = (typeof SLIDESHOW_TEXT_PLACEMENTS)[number];

export const SLIDESHOW_TEXT_PLACEMENT_OPTIONS: {
  value: SlideshowTextPlacement;
  label: string;
}[] = [
  { value: "upper", label: "Upper" },
  { value: "upperThird", label: "Upper third" },
  { value: "center", label: "Center" },
  { value: "belowCenter", label: "Below center" },
];

// ── Bundled fonts ────────────────────────────────────────────────────────────
// One entry per TTF shipped in server/assets/fonts (installed into ~/.fonts at
// boot so librsvg/fontconfig can resolve them) plus the always-present DejaVu
// system face. `fontFamily` is the SVG font-family stack the compositor emits;
// `avgAdvance` is the approximate average glyph advance (fraction of font
// size) the word-wrap estimator uses, matching the values already tuned in
// STYLE_TEXT_OVERLAYS. `cssFallback` gives the dialog preview a rough
// same-category stand-in used while the real font is still loading (or if it
// fails to load). `file` is the TTF's file name inside server/assets/fonts —
// the browser fetches it from GET /api/slideshow-fonts/:file so the Manage
// Styles picker/preview can render each option in its actual typeface. null =
// no bundled file to serve (system-only face like DejaVu), keep the fallback.
export interface SlideshowBundledFont {
  key: string;
  label: string;
  fontFamily: string;
  fontWeight: "normal" | "bold";
  avgAdvance: number;
  cssFallback: string;
  file: string | null;
}

export const SLIDESHOW_BUNDLED_FONTS: SlideshowBundledFont[] = [
  { key: "dejavu_sans_bold", label: "DejaVu Sans Bold (default)", fontFamily: "DejaVu Sans, sans-serif", fontWeight: "bold", avgAdvance: 0.56, cssFallback: "system-ui, sans-serif", file: null },
  { key: "inter_medium", label: "Inter Medium", fontFamily: "Inter Medium, Inter, sans-serif", fontWeight: "normal", avgAdvance: 0.53, cssFallback: "Inter, system-ui, sans-serif", file: "Inter-Medium.ttf" },
  { key: "anton", label: "Anton", fontFamily: "Anton, sans-serif", fontWeight: "normal", avgAdvance: 0.5, cssFallback: "Impact, system-ui, sans-serif", file: "Anton-Regular.ttf" },
  { key: "cormorant_garamond_light", label: "Cormorant Garamond Light", fontFamily: "Cormorant Garamond Light, Cormorant Garamond, serif", fontWeight: "normal", avgAdvance: 0.44, cssFallback: "Garamond, Georgia, serif", file: "CormorantGaramond-Light.ttf" },
  { key: "cormorant_garamond", label: "Cormorant Garamond", fontFamily: "Cormorant Garamond, serif", fontWeight: "normal", avgAdvance: 0.44, cssFallback: "Garamond, Georgia, serif", file: "CormorantGaramond-Regular.ttf" },
  { key: "cormorant_garamond_medium", label: "Cormorant Garamond Medium", fontFamily: "Cormorant Garamond Medium, Cormorant Garamond, serif", fontWeight: "normal", avgAdvance: 0.44, cssFallback: "Garamond, Georgia, serif", file: "CormorantGaramond-Medium.ttf" },
  { key: "cormorant_garamond_semibold", label: "Cormorant Garamond SemiBold", fontFamily: "Cormorant Garamond SemiBold, Cormorant Garamond, serif", fontWeight: "normal", avgAdvance: 0.44, cssFallback: "Garamond, Georgia, serif", file: "CormorantGaramond-SemiBold.ttf" },
  { key: "eb_garamond", label: "EB Garamond", fontFamily: "EB Garamond, serif", fontWeight: "normal", avgAdvance: 0.48, cssFallback: "Garamond, Georgia, serif", file: "EBGaramond-Regular.ttf" },
  { key: "eb_garamond_medium", label: "EB Garamond Medium", fontFamily: "EB Garamond Medium, EB Garamond, serif", fontWeight: "normal", avgAdvance: 0.48, cssFallback: "Garamond, Georgia, serif", file: "EBGaramond-Medium.ttf" },
  { key: "libre_baskerville", label: "Libre Baskerville", fontFamily: "Libre Baskerville, serif", fontWeight: "normal", avgAdvance: 0.58, cssFallback: "Baskerville, Georgia, serif", file: "LibreBaskerville-Regular.ttf" },
  { key: "libre_baskerville_bold", label: "Libre Baskerville Bold", fontFamily: "Libre Baskerville, serif", fontWeight: "bold", avgAdvance: 0.58, cssFallback: "Baskerville, Georgia, serif", file: "LibreBaskerville-Bold.ttf" },
  { key: "playfair_display_semibold", label: "Playfair Display SemiBold", fontFamily: "Playfair Display SemiBold, Playfair Display, serif", fontWeight: "normal", avgAdvance: 0.54, cssFallback: "Georgia, serif", file: "PlayfairDisplay-SemiBold.ttf" },
  { key: "courier_prime", label: "Courier Prime", fontFamily: "Courier Prime, monospace", fontWeight: "normal", avgAdvance: 0.6, cssFallback: "Courier New, monospace", file: "CourierPrime-Regular.ttf" },
  { key: "courier_prime_bold", label: "Courier Prime Bold", fontFamily: "Courier Prime, monospace", fontWeight: "bold", avgAdvance: 0.6, cssFallback: "Courier New, monospace", file: "CourierPrime-Bold.ttf" },
  { key: "great_vibes", label: "Great Vibes (script)", fontFamily: "Great Vibes, cursive", fontWeight: "normal", avgAdvance: 0.42, cssFallback: "cursive", file: "GreatVibes-Regular.ttf" },
];

export function getSlideshowBundledFont(
  key: string | null | undefined,
): SlideshowBundledFont | undefined {
  if (!key) return undefined;
  return SLIDESHOW_BUNDLED_FONTS.find((f) => f.key === key);
}

// ── Settings schema ──────────────────────────────────────────────────────────
// Uploaded custom font: the R2 (or dev /uploads) URL of the stored TTF/OTF,
// plus the family name fc-scan reported at upload time (the compositor installs
// the file into ~/.fonts and references this family in the SVG).
export const slideshowCustomFontSchema = z.object({
  url: z.string().min(1),
  family: z.string().min(1),
  fileName: z.string().nullish(),
});
export type SlideshowCustomFont = z.infer<typeof slideshowCustomFontSchema>;

const hexColor = z
  .string()
  .regex(/^#[0-9a-fA-F]{6}$/, "Use a 6-digit hex color like #FFFFFF");

// All fields optional/nullable: null or absent = keep the style's hardcoded
// default for that knob. `.strip()` (default) drops unknown keys.
export const slideshowTextSettingsSchema = z.object({
  // Bundled font key (SLIDESHOW_BUNDLED_FONTS). Ignored when customFont is set.
  fontKey: z.string().nullish(),
  // Uploaded custom font — wins over fontKey when present.
  customFont: slideshowCustomFontSchema.nullish(),
  color: hexColor.nullish(),
  // Font size in px on a 1080-wide slide (scaled at render like today).
  sizePx: z.coerce.number().int().min(16).max(240).nullish(),
  // Stroke/outline: null = style default behavior; false = off; true = on with
  // the color/width below (falling back to the auto-contrast defaults).
  strokeEnabled: z.boolean().nullish(),
  strokeColor: hexColor.nullish(),
  // Stroke width in px on a 1080-wide slide.
  strokeWidthPx: z.coerce.number().min(0).max(40).nullish(),
  align: z.enum(SLIDESHOW_TEXT_ALIGNMENTS).nullish(),
  placement: z.enum(SLIDESHOW_TEXT_PLACEMENTS).nullish(),
});
export type SlideshowTextSettings = z.infer<typeof slideshowTextSettingsSchema>;

// True when at least one knob is actually set (non-null), i.e. the settings
// blob would change the render at all.
export function hasAnySlideshowTextSettings(
  s: SlideshowTextSettings | null | undefined,
): boolean {
  if (!s) return false;
  return Object.values(s).some((v) => v !== null && v !== undefined);
}

// Parse an untrusted stored/submitted blob. Returns null for invalid input or
// an effectively-empty blob so callers can treat "no settings" uniformly and
// legacy rows (NULL column) keep the hardcoded look.
export function parseSlideshowTextSettings(
  raw: unknown,
): SlideshowTextSettings | null {
  if (raw == null || typeof raw !== "object") return null;
  const parsed = slideshowTextSettingsSchema.safeParse(raw);
  if (!parsed.success) return null;
  return hasAnySlideshowTextSettings(parsed.data) ? parsed.data : null;
}
